// Copyright 2026, Imprivata, Inc.  All rights reserved.

import {
  createIdvConnection,
  deleteIdvConnection,
  fetchOidcDiscovery,
  findIdvConnections,
  getIdvConnection,
  updateIdvConnection,
} from '@api/portalServices';
import type {
  IdvConnection,
  IdvConnectionCreateRequest,
  IdvConnectionSummary,
  IdvConnectionUpdateRequest,
  OidcIntegrationInfo,
} from '@api/types';
import { IdvConnectionTemplate, IdvScope } from '@api/types';
import { createCodingCtxV2 } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import type { ApiError } from '@errorHandler/errors';
import { AppError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * React Query data layer for the IDV connections page. Wraps the list query and the
 * create/get(edit)/update/delete BFF calls so the container holds no imperative fetch logic.
 *
 * - `connections` list: render-driven, so a `useQuery`. Errors are surfaced through the shared
 *   error context from inside the queryFn (then rethrown so the query still enters its error
 *   state) rather than via a separate effect.
 * - edit load: a read triggered on demand (user clicks Edit) whose result must be returned
 *   inline to pre-fill the form, so it goes through the query cache via `queryClient.fetchQuery`
 *   rather than a `useMutation` — a read belongs in the query system, not a mutation.
 * - create/update/delete: writes triggered by user actions, so `useMutation`.
 */
// Reconcile the wire shape of a connection with what the UI expects, inverting the two
// write-side transforms the backend contract forces on Custom connections:
//   1. `templateType`: Custom carries no backend `product_id`, so it comes back empty —
//      resolve that absence to CUSTOM so the UI's `templateType === CUSTOM` branches (badge,
//      edit form, success summary) keep working. Presets carry their product_id, untouched.
//   2. `scope`: the backend requires and echoes `openid`, but the UI treats openid as implicit
//      (the form grid manages only the optional scopes; the summary renders openid separately),
//      so strip it here to avoid a phantom checkbox value and a doubled summary entry.
// Returns the connection with `templateType` narrowed to a concrete IdvConnectionTemplate (the
// empty-string wire value for Custom is resolved to CUSTOM), so downstream UI never handles ''.
const normalizeConnection = <T extends OidcIntegrationInfo>(
  connection: T,
): T & { templateType: IdvConnectionTemplate } => {
  const templateType = connection.templateType || IdvConnectionTemplate.CUSTOM;
  const scope = connection.scope?.filter(s => s !== IdvScope.OPENID);
  return { ...connection, templateType, ...(scope && { scope }) };
};

export const useIdvConnections = () => {
  const { emitError } = useErrorContext();
  const { tenantId } = useSession();
  const queryClient = useQueryClient();

  const {
    data: connections = [],
    isLoading,
    refetch: refetchConnections,
  } = useQuery<IdvConnectionSummary[], ApiError>({
    queryKey: [QueryKey.IDV_CONNECTIONS],
    queryFn: async () => {
      try {
        const res = await findIdvConnections();
        return (res.oidcIntegrations ?? []).map(normalizeConnection);
      } catch (e) {
        emitError(new AppError(e as ApiError));
        throw e;
      }
    },
    retry: false,
  });

  // Refetch the list after a write; returns false if the refetch itself failed so callers
  // can skip their success path.
  const reloadConnections = async (): Promise<boolean> => {
    const { isError } = await refetchConnections();
    return !isError;
  };

  // Enriches a connection with the tenant's OIDC discovery endpoints (authorization/token URLs),
  // which are fetched from a separate resource rather than the connection response. Best-effort —
  // a discovery failure returns the connection unchanged. Folded into both the edit-fetch and
  // create mutations so enrichment is never the caller's responsibility (no un-enriched
  // connection can reach the success/edit screens).
  // Generic over the connection shape so the input type is preserved (e.g. the create
  // response's `clientSecret` survives) while the UI-only discovery fields are layered on.
  const enrichWithDiscovery = async <T extends OidcIntegrationInfo>(
    connection: T,
  ): Promise<
    Omit<T, 'templateType'> &
      Pick<IdvConnection, 'templateType' | 'authorizationEndpointUrl' | 'tokenEndpointUrl'>
  > => {
    const discovery = tenantId
      ? await fetchOidcDiscovery(tenantId).catch(e => {
          emitError(new AppError(e as ApiError));
          return null;
        })
      : null;
    return {
      ...normalizeConnection(connection),
      ...(discovery?.authorization_endpoint && {
        authorizationEndpointUrl: discovery.authorization_endpoint,
        tokenEndpointUrl: discovery.token_endpoint,
      }),
    };
  };

  // Loading a connection for edit gets the connection, then enriches it with discovery endpoints.
  // Uses `fetchQuery` rather than
  // `useQuery` because the load is imperative (fired when the user
  // clicks Edit) and its result is awaited inline to pre-fill the form; a keyed cache entry per
  // clientId still lets React Query dedupe and cache the read. `staleTime: 0` keeps every open
  // fresh — the connection may have changed since it was last edited.
  const fetchConnectionForEdit = (summary: IdvConnectionSummary): Promise<IdvConnection> =>
    queryClient.fetchQuery({
      queryKey: [QueryKey.IDV_CONNECTION, summary.clientId],
      queryFn: async () =>
        enrichWithDiscovery(await getIdvConnection({ clientId: summary.clientId })),
      staleTime: 0,
    });
  // Drives the table's edit-load spinner: true while any per-connection edit fetch is in flight.
  const isEditLoading = useIsFetching({ queryKey: [QueryKey.IDV_CONNECTION] }) > 0;

  // createIdvConnection needs a per-request CodingContext to decrypt the returned client secret,
  // so it is built inside the mutationFn and returned alongside the response. The response is
  // enriched with discovery endpoints here so callers receive the same enriched shape as the
  // edit path and never have to run enrichment themselves.
  const { mutateAsync: createConnection, isPending: isCreating } = useMutation({
    mutationFn: async (payload: IdvConnectionCreateRequest) => {
      const codingCtx = await createCodingCtxV2();
      const response = await createIdvConnection(payload, codingCtx);
      return { response: await enrichWithDiscovery(response), codingCtx };
    },
  });

  const { mutateAsync: updateConnection, isPending: isUpdating } = useMutation({
    mutationFn: (payload: IdvConnectionUpdateRequest) => updateIdvConnection(payload),
  });

  const { mutateAsync: deleteConnection, isPending: isDeleting } = useMutation({
    mutationFn: (clientId: string) => deleteIdvConnection({ clientId }),
  });

  // The OIDC discovery document's `scopes_supported` IS the Custom form's optional-scope
  // checkbox list (rendered dynamically, all unchecked by default). It must be available the
  // moment the Add modal opens — before any connection exists — so this is a render-driven
  // read: a `useQuery` keyed by tenant. (The enrich path calls `fetchOidcDiscovery` directly and
  // does not go through this cache — its fetch is scoped to a single create/edit.)
  // Best-effort: on failure `discovery` stays undefined, so `supportedScopes` is empty and only
  // the locked OpenID row renders. It must NOT surface a banner — the integrations page also
  // serves EpicCare Link / ServiceNow users who never touch the Custom flow, so a discovery
  // failure is silent here (nothing consumes this query's error state), not an "unexpected error"
  // banner shown to every visitor.
  //
  // The failure is intentionally NOT caught to `null`: swallowing it would cache an empty result
  // as a *success*, which `staleTime: Infinity` would then freeze forever (optional scopes would
  // never reappear without a full reload). Letting the query reject instead leaves it in an error
  // state — React Query's default retry + refetch-on-mount/focus recover from a transient failure.
  // `staleTime: Infinity` still applies to a genuine success, which is correct: the discovery doc
  // is static per tenant, so a resolved list never needs re-fetching within a session.
  const { data: discovery } = useQuery({
    queryKey: [QueryKey.IDV_OIDC_DISCOVERY, tenantId],
    queryFn: () => (tenantId ? fetchOidcDiscovery(tenantId) : null),
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Optional scopes the tenant supports: the advertised list minus the always-required
  // `openid` (rendered separately as a locked control). Empty until discovery resolves.
  const supportedScopes = (discovery?.scopes_supported ?? []).filter(s => s !== IdvScope.OPENID);

  return {
    connections,
    isLoading,
    reloadConnections,
    fetchConnectionForEdit,
    isEditLoading,
    createConnection,
    updateConnection,
    deleteConnection,
    isDeleting,
    // True while a create or update is in flight — drives the modal Save button's loading state.
    isSubmitting: isCreating || isUpdating,
    // Optional scopes advertised by the tenant's OIDC discovery doc; drives the form's checkboxes.
    supportedScopes,
  };
};
